import type { ScheduleDefinition, ScheduleOccurrence, ScheduleRunSkipReason } from './types'

const MINUS = '−'

const safeTimezone = (timezone: string): string => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format()
        return timezone
    } catch {
        return 'UTC'
    }
}

const offsetFor = (value: Date, timezone: string): string => {
    const part = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'longOffset',
    })
        .formatToParts(value)
        .find((item) => item.type === 'timeZoneName')?.value

    if (!part || part === 'GMT') return 'UTC+00:00'
    const offset = part.replace('GMT', '')
    return `UTC${offset === '+0' ? '+00:00' : offset.replace('-', MINUS)}`
}

/** "3pm", never "3:00pm" — the trailing :00 is noise once the suffix is lowercase. */
const timeOfDay = (hour: number, minute: number): string => {
    const suffix = hour < 12 ? 'am' : 'pm'
    const hour12 = hour % 12 || 12
    return minute === 0
        ? `${hour12}${suffix}`
        : `${hour12}:${String(minute).padStart(2, '0')}${suffix}`
}

/** hour/minute in the target zone, independent of the browser's own zone or locale. */
const zonedTimeParts = (date: Date, timezone: string): { hour: number; minute: number } => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(date)
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '0'
    return { hour: Number(get('hour')), minute: Number(get('minute')) }
}

/** Formats an instant in the profile/schedule time zone, never the browser zone. */
export const formatScheduleDateTime = (
    value: string | Date,
    timezone: string,
    locale?: string,
): string => {
    const date = typeof value === 'string' ? new Date(value) : value
    if (Number.isNaN(date.getTime())) return String(value)
    const zone = safeTimezone(timezone)
    const datePart = new Intl.DateTimeFormat(locale, {
        timeZone: zone,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date)
    const { hour, minute } = zonedTimeParts(date, zone)
    return `${datePart} ${timeOfDay(hour, minute)}`
}

export const formatScheduleTimeZone = (
    timezone: string,
    at: string | Date = new Date(),
): string => {
    const date = typeof at === 'string' ? new Date(at) : at
    const effectiveTimezone = safeTimezone(timezone)
    return `${effectiveTimezone} (${offsetFor(date, effectiveTimezone)})`
}

/** The short form used next to a formatted occurrence — "UTC", "PST", never an offset. */
export const formatScheduleTimeZoneAbbreviation = (
    timezone: string,
    at: string | Date = new Date(),
): string => {
    const date = typeof at === 'string' ? new Date(at) : at
    const zone = safeTimezone(timezone)
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
        .formatToParts(date)
        .find((item) => item.type === 'timeZoneName')?.value
    return part ?? zone
}

export interface FormatScheduleDisplayOptions {
    locale?: string
    /** Append the time zone abbreviation (e.g. "UTC", "PST"). Defaults to true. */
    showTimeZone?: boolean
}

export interface FormatScheduleOccurrenceOptions extends FormatScheduleDisplayOptions {
    /** Prefix the occurrence with its short weekday in the selected time zone. Defaults to false. */
    showWeekday?: boolean
}

export const formatScheduleOccurrence = (
    value: string | Date | ScheduleOccurrence,
    timezone: string,
    options?: string | FormatScheduleOccurrenceOptions,
): string => {
    const {
        locale,
        showTimeZone = true,
        showWeekday = false,
    } = typeof options === 'string' ? { locale: options } : (options ?? {})
    const instant = typeof value === 'object' && !(value instanceof Date) ? value.utc : value
    const clock = formatScheduleDateTime(instant, timezone, locale)
    const date = typeof instant === 'string' ? new Date(instant) : instant
    const weekday =
        showWeekday && !Number.isNaN(date.getTime())
            ? `${new Intl.DateTimeFormat(locale, {
                  timeZone: safeTimezone(timezone),
                  weekday: 'short',
              }).format(date)}, `
            : ''
    const formatted = `${weekday}${clock}`
    return showTimeZone
        ? `${formatted} ${formatScheduleTimeZoneAbbreviation(timezone, instant)}`
        : formatted
}

const weekdayNames: Record<number, string> = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
}

const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

type CronField = 'minute' | 'hour' | 'dayOfMonth' | 'month' | 'weekday'

interface CronPart {
    start: number
    end: number
    step: number | null
    wildcard: boolean
}

interface ParsedCronField {
    wildcard: boolean
    parts: CronPart[]
    values: number[]
}

const fieldBounds: Record<CronField, readonly [number, number]> = {
    minute: [0, 59],
    hour: [0, 23],
    dayOfMonth: [1, 31],
    month: [1, 12],
    weekday: [0, 7],
}

const aliases: Partial<Record<CronField, Record<string, number>>> = {
    month: Object.fromEntries(
        monthNames.map((name, index) => [name.slice(0, 3).toUpperCase(), index + 1]),
    ),
    weekday: {
        SUN: 0,
        MON: 1,
        TUE: 2,
        WED: 3,
        THU: 4,
        FRI: 5,
        SAT: 6,
    },
}

const joinWords = (values: string[]): string => {
    if (values.length === 1) return values[0]
    if (values.length === 2) return `${values[0]} and ${values[1]}`
    return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`
}

const ordinal = (value: number): string => {
    const lastTwo = value % 100
    const suffix =
        lastTwo >= 11 && lastTwo <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][value % 10] ?? 'th')
    return `${value}${suffix}`
}

const parseCronValue = (value: string, field: CronField): number | null => {
    const alias = aliases[field]?.[value.toUpperCase()]
    const parsed = alias ?? (/^\d+$/.test(value) ? Number(value) : Number.NaN)
    const [minimum, maximum] = fieldBounds[field]
    if (!Number.isInteger(parsed) || parsed < minimum || parsed > maximum) return null
    return parsed
}

/**
 * This is intentionally a display parser, not a cron validator. The service
 * remains responsible for accepted syntax, occurrence limits, and impossible
 * dates. It only recognizes conventional numeric/name lists, ranges, and steps.
 */
const parseCronField = (value: string, field: CronField): ParsedCronField | null => {
    if (!value) return null
    const parts: CronPart[] = []
    const values = new Set<number>()

    for (const item of value.split(',')) {
        const match = item.match(/^([^/]+?)(?:\/(\d+))?$/)
        if (!match) return null
        const [, base, stepText] = match
        const step = stepText ? Number(stepText) : null
        if (step !== null && (!Number.isInteger(step) || step < 1)) return null

        let start: number
        let end: number
        let wildcard = false
        if (base === '*') {
            ;[start, end] = fieldBounds[field]
            wildcard = true
        } else {
            const range = base.split('-')
            if (range.length > 2 || range.some((part) => !part)) return null
            const first = parseCronValue(range[0], field)
            const last = range.length === 2 ? parseCronValue(range[1], field) : first
            if (first === null || last === null || first > last) return null
            start = first
            end = last
        }

        for (let number = start; number <= end; number += step ?? 1)
            values.add(field === 'weekday' && number === 7 ? 0 : number)
        parts.push({ start, end, step, wildcard })
    }

    return {
        wildcard: value === '*',
        parts,
        values: Array.from(values).sort((left, right) => left - right),
    }
}

const describeTime = (minute: ParsedCronField, hour: ParsedCronField): string => {
    const minuteStep = minute.parts.length === 1 ? minute.parts[0] : null
    const hourStep = hour.parts.length === 1 ? hour.parts[0] : null

    if (minute.wildcard && hour.wildcard) return 'Every minute'
    if (minuteStep?.wildcard && minuteStep.step && hour.wildcard)
        return `Every ${minuteStep.step} minutes`
    if (hourStep?.wildcard && hourStep.step && minute.values.length === 1)
        return `Every ${hourStep.step} hours at ${String(minute.values[0]).padStart(2, '0')} minutes past`
    if (hour.wildcard && minute.values.length === 1)
        return `At ${String(minute.values[0]).padStart(2, '0')} minutes past every hour`

    const times = hour.values.flatMap((hourValue) =>
        minute.values.map((minuteValue) => timeOfDay(hourValue, minuteValue)),
    )
    return times.length <= 8 ? `At ${joinWords(times)}` : 'At the selected times'
}

const describePart = (part: CronPart, name: (value: number) => string): string => {
    const range =
        part.start === part.end ? name(part.start) : `${name(part.start)}–${name(part.end)}`
    if (!part.step) return range
    return part.wildcard ? `every ${part.step}` : `every ${part.step} from ${range}`
}

const describeWeekdays = (weekday: ParsedCronField): string => {
    if (weekday.wildcard) return 'day'
    if (
        weekday.parts.length === 1 &&
        weekday.parts[0].start === 1 &&
        weekday.parts[0].end === 5 &&
        !weekday.parts[0].step
    )
        return 'weekday'
    return joinWords(
        weekday.parts.map((part) =>
            describePart(part, (value) => weekdayNames[value === 7 ? 0 : value]),
        ),
    )
}

const describeDaysOfMonth = (dayOfMonth: ParsedCronField): string =>
    joinWords(dayOfMonth.parts.map((part) => describePart(part, ordinal)))

const describeMonths = (month: ParsedCronField): string =>
    joinWords(month.parts.map((part) => describePart(part, (value) => monthNames[value - 1])))

const describeCalendar = (
    dayOfMonth: ParsedCronField,
    month: ParsedCronField,
    weekday: ParsedCronField,
): string => {
    const hasDate = !dayOfMonth.wildcard || !month.wildcard
    const hasWeekday = !weekday.wildcard
    if (!hasDate && !hasWeekday) return 'Every day'
    if (!hasDate) return `Every ${describeWeekdays(weekday)}`

    const date = dayOfMonth.wildcard
        ? `every day in ${describeMonths(month)}`
        : month.wildcard
          ? `the ${describeDaysOfMonth(dayOfMonth)} of every month`
          : `${describeMonths(month)} ${describeDaysOfMonth(dayOfMonth)}`
    return hasWeekday ? `On ${date} or every ${describeWeekdays(weekday)}` : date
}

const hasPossibleCalendarDate = (dayOfMonth: ParsedCronField, month: ParsedCronField): boolean => {
    if (dayOfMonth.wildcard) return true
    const months = month.wildcard
        ? Array.from({ length: 12 }, (_, index) => index + 1)
        : month.values
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
    return months.some((monthValue) =>
        dayOfMonth.values.some((dayValue) => dayValue <= daysInMonth[monthValue - 1]),
    )
}

const unparsedScheduleSummary = (expression: string, zoneSuffix: string): string =>
    `Schedule expression: ${expression}${zoneSuffix}. Preview it to check this schedule.`

/** A concise natural-language display for common five-field schedule expressions. */
export const formatScheduleSummary = (
    schedule: ScheduleDefinition,
    options?: string | FormatScheduleDisplayOptions,
): string => {
    const { locale, showTimeZone = true } =
        typeof options === 'string' ? { locale: options } : (options ?? {})

    if (schedule.kind === 'once') {
        return `Once on ${formatScheduleOccurrence(schedule.run_once_at, schedule.timezone, { locale, showTimeZone })}`
    }

    const fields = schedule.cron_expression.trim().split(/\s+/)
    const zone = showTimeZone ? ` ${formatScheduleTimeZoneAbbreviation(schedule.timezone)}` : ''
    if (fields.length !== 5) return unparsedScheduleSummary(schedule.cron_expression, zone)
    const [minuteText, hourText, dayOfMonthText, monthText, weekdayText] = fields
    const minute = parseCronField(minuteText, 'minute')
    const hour = parseCronField(hourText, 'hour')
    const dayOfMonth = parseCronField(dayOfMonthText, 'dayOfMonth')
    const month = parseCronField(monthText, 'month')
    const weekday = parseCronField(weekdayText, 'weekday')
    if (
        !minute ||
        !hour ||
        !dayOfMonth ||
        !month ||
        !weekday ||
        dayOfMonth.parts.some((part) => part.step) ||
        month.parts.some((part) => part.step) ||
        weekday.parts.some((part) => part.step) ||
        (weekday.wildcard && !hasPossibleCalendarDate(dayOfMonth, month))
    )
        return unparsedScheduleSummary(schedule.cron_expression, zone)

    const time = describeTime(minute, hour)
    const calendar = describeCalendar(dayOfMonth, month, weekday)
    if (calendar === 'Every day')
        return time.startsWith('At ') ? `${calendar} at ${time.slice(3)}${zone}` : `${time}${zone}`
    if (calendar.startsWith('Every '))
        return time.startsWith('At ')
            ? `${calendar} at ${time.slice(3)}${zone}`
            : `${time} on ${calendar.toLowerCase()}${zone}`
    return `${time}, ${calendar}${zone}`
}

export const SCHEDULE_SKIP_REASON_COPY: Record<ScheduleRunSkipReason, string> = {
    skipped_ad_type_unsupported: 'Skipped: scheduling is not available for this segment type yet.',
    skipped_disabled: 'Skipped: this segment is disabled.',
    skipped_frequency_conflict: 'Skipped: this segment is no longer set to manual frequency.',
    skipped_no_active_subscription: 'Skipped: this account does not have an active subscription.',
    skipped_no_sp_access: 'Skipped: this profile does not have Amazon Sponsored Products access.',
    skipped_paused: 'Skipped: this segment is paused.',
    skipped_profile_unlinked: 'Skipped: this profile is no longer linked.',
    skipped_rollout_not_allowlisted:
        'Skipped: scheduled runs are not enabled for this profile yet.',
    skipped_rollout_off: 'Skipped: scheduled runs are temporarily unavailable.',
    skipped_schedule_changed: 'Skipped: this schedule changed before the run started.',
    skipped_shadow_mode:
        'Skipped: this scheduled run was recorded for testing and was not executed.',
    skipped_stale_profile_update:
        'Skipped: this profile has not had a successful data sync recently.',
    skipped_too_late: 'Skipped: this run could not start within 45 minutes of its scheduled time.',
}

export const formatScheduleSkipReason = (reason: string | null): string | null => {
    if (!reason) return null
    return (
        SCHEDULE_SKIP_REASON_COPY[reason as ScheduleRunSkipReason] ??
        'Skipped: this scheduled run could not be completed.'
    )
}
